<template>
  <div class="list-purchase-orders">
    <AppPageHeader title="Purchase Orders" :action="{ title: 'Add new purchase order', to: { name: 'CreatePurchaseOrder' } }" />

    <div class="card">
      <BaseTable
        :fields="tableFields"
        :items="results ?? []"
        :is-loading="isPending"
        :is-searchable="true"
        :clickable="true"
        :paginate="true"
        :error="error"
        :next-page="nextPage"
        :prev-page="prevPage"
        :page="currentPage"
        :total-records="totalRecords"
        :total-pages="totalPages"
        :number-per-page="numPerPage"
        @on-search="(val) => search(val)"
        @row-selected="onRowSelected"
      >
        <template #purchaseOrderId-data="{ row }">
          <router-link :to="{ name: 'ShowPurchaseOrder', params: { id: row.id } }">{{ row.purchaseOrderId }}</router-link>
        </template>

        <template #status-data="{ row }">
          <BaseBadge
            :variant="row.status === PURCHASE_ORDER_STATUS.CLOSED ? 'success' : 'warning'"
          >
            <span class="capitalize">{{ row.status }}</span>
          </BaseBadge>
        </template>

        <template #date-data="{ row }">
          <span>{{ formatDate(row.date) }}</span>
        </template>

        <template #vendorId-data="{ row }">
          <router-link v-if="vendors?.find(vendor => vendor.id === row.vendorId)?.id" :to="{ name: 'ShowVendor', params: { id: row.vendorId } }">
            {{ vendors?.find(vendor => vendor.id === row.vendorId)?.name || row.vendorId }}
          </router-link>
          <span v-else>
            {{ row.vendorId }}
          </span>
        </template>

        <template #projectId-data="{ row }">
          <router-link v-if="projects?.find(project => project.id === row.projectId)?.id" :to="{ name: 'ShowProject', params: { projectId: row.projectId, customerId: projects?.find(project => project.id === row.projectId)?.customerId } }">
            {{ projects?.find(project => project.id === row.projectId)?.name || row.projectId }}
          </router-link>
          <span v-else>
            {{ row.projectId }}
          </span>
        </template>

        <template #total-data="{ row }">
          {{ currency(row.total) }}
        </template>
      </BaseTable>
    </div>
    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      title="Delete Purchase Order"
      @delete="deletePurchaseOrder"
    >
      <template #modal-body>
        <p>Are you sure you want to delete this purchase order?</p>
        <p>This will also delete:</p>
        <ul>
          <li>All bills</li>
        </ul>
      </template>
    </DeleteConfirmationModal>
  </div>
</template>

<script lang="ts" setup>
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import { computed, ref } from 'vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { useHead } from '@unhead/vue'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import { useSearchablePagination } from '@/composables/useSearchablePagination'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { PURCHASE_ORDER_STATUS, PurchaseOrder } from '@/models/purchaseOrder'
import { formatDate, currency } from '@/utils'
import ProjectService from '@/services/ProjectService'
import { inArray } from '@/utils/queryFilters'
import useGetAllvendors from '@/composables/useGetAllVendors'
import { useNotification } from '@kyvg/vue3-notification'
import { useRouter } from 'vue-router'

useHead({
  title: 'Purchase Orders'
})

const { notify } = useNotification()
const $router = useRouter()
const tableFields = [
  { key: 'purchaseOrderId', label: 'PO#', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'date', label: 'Date', sortable: true },
  { key: 'vendorId', label: 'Vendor' },
  { key: 'projectId', label: 'Project' },
  { key: 'total', label: 'Total' },
]

const { data: vendors  } = useGetAllvendors()
const numPerPage = ref(25)

const {
  results,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination({
  indexName: 'purchase_orders',
  service: new PurchaseOrderService(''),
  numPerPage: numPerPage.value,
  defaultOrderBy: 'createdAt',
  defaultOrderDir: 'desc'
})

const onRowSelected = (row: any) => {
  $router.push({ name: 'ShowPurchaseOrder', params: { id: row.id } })
}

const uniqueProjects = computed(() => {
  return results.value?.map((purchaseOrder: PurchaseOrder) => purchaseOrder.projectId)
})

const projectService = new ProjectService()
const { data: projects } = useQuery({
  queryKey: ['projects', uniqueProjects, currentPage],
  queryFn: () => projectService.where([inArray('id', uniqueProjects.value)], [])
})

const { mutate: deletePurchaseOrder } = useMutation({
  onMutate: async (purchaseOrder: PurchaseOrder) => {
    const purchaseOrderService = new PurchaseOrderService('')
    await purchaseOrderService.delete(purchaseOrder.id)
    return purchaseOrder
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Purchase order deleted successfully',
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Failed to delete purchase order',
      text: error.message,
    })
  },
})

</script>
